import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Star, Filter } from "lucide-react";
import { useSafeFetch } from "../lib/useSafeFetch";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "luxury", label: "Luxury" },
  { id: "sports", label: "Sports" },
  { id: "suv", label: "SUV" },
  { id: "electric", label: "Electric" },
  { id: "economy", label: "Economy" },
];

export default function Vehicles() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const transmission = params.get("transmission") || "all";

  const query = {};
  if (category !== "all") query.category = category;
  if (transmission !== "all") query.transmission = transmission;

  const { data: vehicles, loading } = useSafeFetch("/vehicles", {
    defaultValue: [], asList: true, params: query,
  });

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v === "all") p.delete(k);
    else p.set(k, v);
    setParams(p);
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 md:py-16" data-testid="vehicles-page">
      <div className="mb-10">
        <p className="overline mb-3">The fleet</p>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight">
          Find your <em className="italic">drive</em>.
        </h1>
        <p className="mt-4 text-neutral-600 max-w-xl">
          {vehicles.length} vehicles ready to be reserved. Free cancellation up to 24 hours.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setParam("category", c.id)}
            className={`px-5 py-2 text-sm transition-colors ${
              category === c.id
                ? "bg-black text-white"
                : "bg-transparent border border-black/15 hover:border-black"
            }`}
            data-testid={`filter-category-${c.id}`}
          >
            {c.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Filter size={14} className="text-neutral-500" />
          <select
            value={transmission}
            onChange={(e) => setParam("transmission", e.target.value)}
            className="bg-transparent border-b border-black/20 py-1 text-sm focus:outline-none focus:border-black"
            data-testid="filter-transmission"
          >
            <option value="all">All transmissions</option>
            <option value="automatic">Automatic</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      </div>

      <div className="divider-line mb-8" />

      {loading && <p className="text-neutral-500">Loading collection…</p>}
      {!loading && vehicles.length === 0 && (
        <p className="text-neutral-500">No vehicles match your filters.</p>
      )}
      {!loading && vehicles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vehicles.map((v) => (
            <Link
              key={v.id}
              to={`/vehicles/${v.id}`}
              className="editorial-card group block"
              data-testid={`vehicle-card-${v.id}`}
            >
              <div className="relative overflow-hidden bg-[#EFECE6]" style={{ aspectRatio: "4/3" }}>
                <img
                  src={v.image_url}
                  alt={v.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 bg-white px-3 py-1 text-xs uppercase tracking-wider">
                  {v.category}
                </div>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider">{v.brand}</p>
                    <p className="font-display text-2xl mt-1">{v.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-2xl">€{Number((v.has_duration_tiers ? v.display_rate : v.daily_rate) || 0).toFixed(0)}</p>
                    <p className="text-xs text-neutral-500">per day</p>
                  </div>
                </div>
                <div className="divider-line my-4"></div>
                <div className="flex items-center justify-between text-xs text-neutral-600">
                  <span className="flex items-center gap-1"><Star size={12} fill="currentColor" /> {v.rating}</span>
                  <span>{v.seats} seats</span>
                  <span>{v.transmission}</span>
                  <span>{v.fuel}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
